import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Download, Eye, Search, RefreshCw, Users, FileCheck } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface PlanWithInterview {
  id: string;
  interview_id: string;
  user_id: string;
  pdf_url: string | null;
  pdf_metadata: any;
  plan_data: any;
  version: number;
  is_latest: boolean;
  created_at: string;
  updated_at: string;
  interview?: {
    name: string;
    email: string | null;
    location: any;
    household_size: number;
    emergency_types: string[];
    status: string;
  };
}

export default function AdminReports() {
  const navigate = useNavigate();
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  
  const [plans, setPlans] = useState<PlanWithInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Stats
  const [stats, setStats] = useState({
    totalPlans: 0,
    totalUsers: 0,
    avgPages: 0,
  });

  useEffect(() => {
    if (!authLoading && !isSuperAdmin) {
      toast.error('Access denied. Super admin required.');
      navigate('/');
      return;
    }
    
    if (isSuperAdmin) {
      fetchPlans();
    }
  }, [isSuperAdmin, authLoading, navigate]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      // Fetch all emergency plans with interview data
      const { data: plansData, error: plansError } = await supabase
        .from('emergency_plans')
        .select('*')
        .order('created_at', { ascending: false });

      if (plansError) throw plansError;

      // Fetch corresponding interviews
      const interviewIds = [...new Set(plansData?.map(p => p.interview_id) || [])];
      
      const { data: interviewsData, error: interviewsError } = await supabase
        .from('interviews')
        .select('id, name, email, location, household_size, emergency_types, status')
        .in('id', interviewIds);

      if (interviewsError) throw interviewsError;

      // Merge data
      const interviewMap = new Map(interviewsData?.map(i => [i.id, i]) || []);
      const mergedPlans = (plansData || []).map(plan => ({
        ...plan,
        interview: interviewMap.get(plan.interview_id),
      }));

      setPlans(mergedPlans);
      
      // Calculate stats
      const uniqueUsers = new Set(mergedPlans.map(p => p.user_id)).size;
      const pageEstimates = mergedPlans
        .map(p => {
          const metadata = p.pdf_metadata as Record<string, any> | null;
          const planData = p.plan_data as Record<string, any> | null;
          return metadata?.estimated_pages || planData?.metadata?.estimated_pages || 0;
        })
        .filter(p => p > 0);
      const avgPages = pageEstimates.length > 0 
        ? Math.round(pageEstimates.reduce((a, b) => a + b, 0) / pageEstimates.length)
        : 0;
      
      setStats({
        totalPlans: mergedPlans.length,
        totalUsers: uniqueUsers,
        avgPages,
      });
      
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (pdfUrl: string | null, name: string) => {
    if (!pdfUrl) {
      toast.error('PDF not available');
      return;
    }
    
    try {
      // Get signed URL from storage
      const { data, error } = await supabase.storage
        .from('emergency-plans')
        .createSignedUrl(pdfUrl.replace('emergency-plans/', ''), 3600);
      
      if (error) throw error;
      
      window.open(data.signedUrl, '_blank');
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const getLocationString = (location: any): string => {
    if (!location) return 'Unknown';
    return `${location.city || ''}, ${location.state || ''}`.trim().replace(/^,|,$/g, '') || 'Unknown';
  };

  const getReadinessScore = (plan: PlanWithInterview): number | null => {
    const metadata = plan.pdf_metadata as Record<string, any> | null;
    const planData = plan.plan_data as Record<string, any> | null;
    return metadata?.readiness_score || 
           planData?.metadata?.readiness_score ||
           planData?.executive_summary?.readiness_score ||
           null;
  };

  const getPageCount = (plan: PlanWithInterview): number | null => {
    const metadata = plan.pdf_metadata as Record<string, any> | null;
    const planData = plan.plan_data as Record<string, any> | null;
    return metadata?.estimated_pages || 
           planData?.metadata?.estimated_pages ||
           null;
  };

  // Filter plans
  const filteredPlans = plans.filter(plan => {
    const matchesSearch = 
      plan.interview?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.interview?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getLocationString(plan.interview?.location).toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || plan.interview?.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">All Emergency Plans</h1>
              <p className="text-muted-foreground">
                Super admin view of all generated emergency plans
              </p>
            </div>
            <Button onClick={fetchPlans} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Plans</CardTitle>
                <FileCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalPlans}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUsers}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Avg Pages</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.avgPages || '—'}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Plans Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Emergency Types</TableHead>
                    <TableHead className="text-center">Pages</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : filteredPlans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No emergency plans found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPlans.map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{plan.interview?.name || 'Unknown'}</p>
                            <p className="text-sm text-muted-foreground">{plan.interview?.email || '—'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getLocationString(plan.interview?.location)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(plan.interview?.emergency_types || []).slice(0, 3).map((type, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {type}
                              </Badge>
                            ))}
                            {(plan.interview?.emergency_types?.length || 0) > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{(plan.interview?.emergency_types?.length || 0) - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {getPageCount(plan) || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {getReadinessScore(plan) !== null ? (
                            <Badge variant={getReadinessScore(plan)! >= 70 ? 'default' : 'secondary'}>
                              {getReadinessScore(plan)}%
                            </Badge>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {format(new Date(plan.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/plan-generation/${plan.interview_id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPDF(plan.pdf_url, plan.interview?.name || 'plan')}
                              disabled={!plan.pdf_url}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
